/* eslint-disable @typescript-eslint/no-explicit-any */
import { isPlatformServer } from '@angular/common'
import { DomSanitizer } from '@angular/platform-browser'

import type QuillType from 'quill'
import type { QuillOptions } from 'quill'
import type DeltaType from 'quill-delta'

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Directive,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  Renderer2,
  SecurityContext,
  signal,
  SimpleChanges,
  ViewEncapsulation,
  DOCUMENT
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { EMPTY, fromEvent, Subject, Subscription } from 'rxjs'
import { catchError, debounceTime, map, mergeMap } from 'rxjs/operators'

import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from '@angular/forms'

import { CustomModule, CustomOption, defaultModules, QuillModules } from './config/public_api'

import Quill from 'quill'
import QuillBetterTable from 'quill-better-table'
import { Mention, MentionBlot } from "quill-mention"
import "quill-mention/autoregister"
import type History from 'quill/modules/history'
import type Toolbar from 'quill/modules/toolbar'
import { format } from 'date-fns';
import { FileTypesDeleteMapper } from '../form-data/post-data'
import { CollaborationService } from '../services/collaboration.service'
import { getFormat, raf$ } from './helpers'
import { QuillService } from './quill.service'
// import AutoLinks from 'quill-auto-links';
// Quill.register('modules/autoLinks', AutoLinks);
import MagicUrl from 'quill-magic-url'
import tippy from 'tippy.js';

Quill.register('modules/magicUrl', MagicUrl)
Quill.register({
  'modules/better-table': QuillBetterTable,
  "blots/mention": MentionBlot,
  "modules/mention": Mention
}, true);

const Font: any = Quill.import('attributors/style/font');
// We do not add Aref Ruqaa since it is the default
Font.whitelist = ['Arial', 'Arial Black', 'Arial Narrow', 'Bookman', 'Calibri', 'Calibri Light', 'Century Gothic', 'Comic Sans MS', 'Courier New', 'Garamond', 'Georgia', 'Helvetica', 'Impact', 'Lucida Console', 'Monospace', 'Palatino Linotype', 'Sans-serif', 'Serif', 'Tahoma', 'Trebuchet MS', 'Times New Roman', 'Verdana'];
Quill.register(Font, true);

var SizeStyle: any = Quill.import('attributors/style/size');
SizeStyle.whitelist = ['8px', '10px', '12px', '14px', '16px', '18px', '20px', '22px', '24px', '36px'];
Quill.register(SizeStyle, true);


export interface Range {
  index: number
  length: number
}

export interface ContentChange {
  content: DeltaType
  delta: DeltaType
  editor: QuillType
  html: string | null
  oldDelta: DeltaType
  source: string
  text: string
}

export interface SelectionChange {
  editor: QuillType
  oldRange: Range | null
  range: Range | null
  source: string
}

export interface Blur {
  editor: QuillType
  source: string
}

export interface Focus {
  editor: QuillType
  source: string
}


class StyledMentionBlot extends MentionBlot {
  static getOOODetails: (data: any, element: any) => void;
  static override render(data: any) {
    const element = document.createElement("span");
    element.classList.add(`at-mention-span`);
    const classToBind = data.denotationChar === "@" ? `at-mention-id-${data.id}` : `file-mention-id-${data.id}`;
    element.classList.add(classToBind);
    element.innerText = data.value;
    if (StyledMentionBlot.getOOODetails) {
      StyledMentionBlot.getOOODetails(data, element);
    }
    return element;
  }
}
StyledMentionBlot.blotName = "styled-mention";
Quill.register(StyledMentionBlot);
export type EditorChangeContent = ContentChange & { event: 'text-change' }
export type EditorChangeSelection = SelectionChange & { event: 'selection-change' }

@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class QuillEditorBase implements AfterViewInit, ControlValueAccessor, OnChanges, OnInit, OnDestroy, Validator {
  readonly format = input<'object' | 'html' | 'text' | 'json' | undefined>(
    undefined
  )
  readonly theme = input<any>(undefined)
  readonly modules = input<QuillModules | undefined>(undefined)
  readonly debug = input<'warn' | 'log' | 'error' | false>(false)
  readonly readOnly = input<any>(false)
  readonly placeholder = input<any>(undefined)
  readonly maxLength = input<any>(undefined)
  readonly minLength = input<any>(undefined)
  readonly required = input(false)
  readonly formats = input<string[] | null | undefined>(undefined)
  readonly customToolbarPosition = input<'top' | 'bottom'>('top')
  readonly sanitize = input<any>(undefined)
  readonly beforeRender = input<any>(undefined)
  readonly styles = input<any>(null)
  readonly registry = input<QuillOptions['registry']>(
    undefined
  )
  readonly bounds = input<HTMLElement | any>(undefined)
  readonly customOptions = input<CustomOption[]>([])
  readonly customModules = input<CustomModule[]>([])
  readonly trackChanges = input<'user' | 'all' | undefined>(undefined)
  readonly classes = input<any>(undefined)
  readonly trimOnValidation = input(false)
  readonly linkPlaceholder = input<any>(undefined)
  readonly compareValues = input(false)
  readonly filterNull = input(false)
  readonly debounceTime = input<any>(undefined);
  readonly atMentionConfig = input<any>(undefined)
  mentionType = '';
  /*
  https://github.com/KillerCodeMonkey/ngx-quill/issues/1257 - fix null value set

  provide default empty value
  by default null

  e.g. defaultEmptyValue="" - empty string

  <quill-editor
    defaultEmptyValue=""
    formControlName="message"
  ></quill-editor>
  */
  readonly defaultEmptyValue = input<any>(null)

  @Output() onEditorCreated: EventEmitter<QuillType> = new EventEmitter()
  @Output() onEditorChanged: EventEmitter<EditorChangeContent | EditorChangeSelection> = new EventEmitter()
  @Output() onContentChanged: EventEmitter<ContentChange> = new EventEmitter()
  @Output() onSelectionChanged: EventEmitter<SelectionChange> = new EventEmitter()
  @Output() onFocus: EventEmitter<Focus> = new EventEmitter()
  @Output() onBlur: EventEmitter<Blur> = new EventEmitter()
  @Output() onNativeFocus: EventEmitter<Focus> = new EventEmitter()
  @Output() onNativeBlur: EventEmitter<Blur> = new EventEmitter()

  quillEditor!: QuillType
  editorElem!: HTMLElement
  content: any
  disabled = false // used to store initial value before ViewInit

  readonly toolbarPosition = signal('top')

  onModelChange!: (modelValue?: any) => void
  onModelTouched!: () => void
  onValidatorChanged!: () => void

  private subscription: Subscription | null = null
  private quillSubscription: Subscription | null = null

  private elementRef = inject(ElementRef)
  private document = inject(DOCUMENT)

  private cd = inject(ChangeDetectorRef)
  private domSanitizer = inject(DomSanitizer)
  private platformId = inject<string>(PLATFORM_ID)
  private renderer = inject(Renderer2)
  private zone = inject(NgZone)
  private service = inject(QuillService)
  private destroyRef = inject(DestroyRef);
  emptyText = false;
  hashValues = [];
  searchTermSubject = new Subject<string>();
  renderList: any;
  isPasting: any;
  ctrlAPressed = false;
  tippyInstances: any = {};
  OOOmessages: any = {};
  constructor(private collaborationService: CollaborationService) {
    StyledMentionBlot.getOOODetails = (data: any, element: any) => {
      if (data?.id && this.tippyInstances?.[data?.id]) {
        this.tippyInstances?.[data?.id].destroy();
        this.tippyInstances[data?.id] = null;
      }
      const email: any = data?.id?.split('--')?.pop();
      if (!email) {
        this.OOOmessages[data?.id] = '';
        return;
      }
      const today: any = new Date();
      today.setMonth(today.getMonth() + 1);
      today.setHours(23, 59, 59, 999);
      this.collaborationService.getOOO(email, today.toISOString()).subscribe({
        next: (response: any) => {
          let OOOmsg: any;
          const OOOItems = response?.outOfOfficeItems;
          if (!OOOItems?.length) {
            OOOmsg = '';
            this.OOOmessages[data?.id] = '';
            return;
          }

          let filteredOOOItem: any;
          const IGNORE_PERIOD = 6 * 60 * 60 * 1000; // 6 hours which can be ignored

          const getOOOmsg = (item: any) => {
            const start = new Date(item.startDateTime);
            const end = new Date(item.endDateTime);
            const displayEnd = new Date(end.getTime() - 1);

            const isSameDay =
              start.getFullYear() === displayEnd.getFullYear() &&
              start.getMonth() === displayEnd.getMonth() &&
              start.getDate() === displayEnd.getDate();

            return isSameDay
              ? 'on ' + format(start, 'MMM-dd-yy')
              : 'starting ' + format(start, 'MMM-dd-yy') + ' to ' + format(displayEnd, 'MMM-dd-yy');
          };

          filteredOOOItem = OOOItems.filter((row: any) => {
            const start = new Date(row.startDateTime);
            const end = new Date(row.endDateTime);
            const durationInMs = Math.abs(end.getTime() - start.getTime());
            return IGNORE_PERIOD <= durationInMs && start <= today && end <= today;
          });

          OOOmsg = filteredOOOItem.length > 0 ? getOOOmsg(filteredOOOItem[0]) : '';

          if (!OOOmsg) {
            this.OOOmessages[data?.id] = '';
            return;
          }
          element.closest('.mention')?.classList.add('mention__ooo-status');

          this.OOOmessages[data?.id] = OOOmsg;

          const mentionElement = this.document.getElementById('at-mention-id-' + data.id);
          this.tippyInstances[data?.id] = tippy(element, {
            content: `<div>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
            style="transform: scale(0.7)">
            <g clip-path="url(#clip0_2554_333643)">
            <path d="M2 24C1.46667 24 1 23.8 0.6 23.4C0.2 23 0 22.5333 0 22V2C0 1.46667 0.2 1 0.6 0.6C1 0.2 1.46667 0 2 0H11.7V2H2V22H11.7V24H2ZM18.2 17.8333L16.7667 16.4L20.1667 13H8.5V11H20.1L16.7 7.6L18.1333 6.16667L24 12.0333L18.2 17.8333Z" fill="#CC30CF"/>
            </g>
            <defs>
            <clipPath id="clip0_2554_333643">
            <rect width="24" height="24" fill="white"/>
            </clipPath>
            </defs>
            </svg>
            
            OOO ${this.OOOmessages[data?.id] ?? OOOmsg}
             </div>`,
            allowHTML: true,
            placement: 'bottom',
            theme: 'myCustomTheme',
            trigger: 'mouseenter focus',
            appendTo: (this.atMentionConfig()?.['token'] ? element.parentElement : mentionElement) || document.body
          });
          this.tippyInstances?.[data?.id].show();
          setTimeout(() => {
            this.tippyInstances?.[data?.id].hide();
          }, 10000)
        }
      })
    }
  }
  static normalizeClassNames(classes: string): string[] {
    const classList = classes.trim().split(' ')
    return classList.reduce((prev: string[], cur: string) => {
      const trimmed = cur.trim()
      if (trimmed) {
        prev.push(trimmed)
      }
      return prev
    }, [])
  }

  valueGetter = input((quillEditor: QuillType): string | any => {
    let html: string | null = quillEditor.getSemanticHTML()
    if (this.isEmptyValue(html)) {
      html = this.defaultEmptyValue()
    }
    let modelValue: string | DeltaType | null = html
    const format = getFormat(this.format(), this.service.config.format)

    if (format === 'text') {
      modelValue = quillEditor.getText()
    } else if (format === 'object') {
      modelValue = quillEditor.getContents()
    } else if (format === 'json') {
      try {
        modelValue = JSON.stringify(quillEditor.getContents())
      } catch (e) {
        modelValue = quillEditor.getText()
      }
    }

    return modelValue
  })

  valueSetter = input((quillEditor: QuillType, value: any): any => {
    const format = getFormat(this.format(), this.service.config.format)
    if (format === 'html') {
      const sanitize = [true, false].includes(this.sanitize()) ? this.sanitize() : (this.service.config.sanitize || false)
      if (sanitize) {
        value = this.domSanitizer.sanitize(SecurityContext.HTML, value)
      }
      return quillEditor.clipboard.convert({ html: value })
    } else if (format === 'json') {
      try {
        return JSON.parse(value)
      } catch (e) {
        return [{ insert: value }]
      }
    }

    return value
  })

  ngOnInit() {
    this.toolbarPosition.set(this.customToolbarPosition());
    this.searchTermSubject.pipe(
      debounceTime(650)
    )
      .subscribe((searchTerm: any) => {
        if (this.mentionType === "@") {
          this.getUsersList(searchTerm);
        } else {
          this.hashValues = [];
          this.getDocumentsList(searchTerm);
        }

      });
  }

  ngAfterViewInit() {
    if (isPlatformServer(this.platformId)) {
      return
    }

    // The `quill-editor` component might be destroyed before the `quill` chunk is loaded and its code is executed
    // this will lead to runtime exceptions, since the code will be executed on DOM nodes that don't exist within the tree.

    this.quillSubscription = this.service.getQuill().pipe(
      mergeMap((Quill) => {
        const promises = [this.service.registerCustomModules(Quill, this.customModules())]
        const beforeRender = this.beforeRender() ?? this.service.config.beforeRender
        if (beforeRender) {
          promises.push(beforeRender())
        }
        return Promise.all(promises).then(() => Quill)
      })
    ).subscribe(Quill => {
      this.editorElem = this.elementRef.nativeElement.querySelector(
        '[quill-editor-element]'
      )

      const fontFamilyMatcher = (node, delta) => {
        const row = node.parentNode;
        const table = row.parentNode.tagName === 'TABLE'
          ? row.parentNode
          : row.parentNode.parentNode;
        const rows = Array.from(table.querySelectorAll('tr'));
        const cells = Array.from(row.querySelectorAll('td'));
        const rowId = rows.indexOf(row) + 1;
        const cellId = cells.indexOf(node) + 1;
        const colspan = node.getAttribute('colspan') || false
        const rowspan = node.getAttribute('rowspan') || false
        const cellBg = node.getAttribute('data-cell-bg') || node.style.backgroundColor || node.style.background // The td from external table has no 'data-cell-bg' 

        if (cellBg && delta.length() === 0) {
          delta.insert('\n', {
            "row": rowId,
            'table-cell-line': { row: rowId, cell: cellId, rowspan, colspan, 'cell-bg': cellBg }
          })
        }
        var fontf = '';
        if (node.style.fontFamily) {
          if (node.style.fontFamily.split('"')[1] && Font.whitelist.includes(node.style.fontFamily.split('"')[1])) {
            fontf = node.style.fontFamily.split('"')[1];
          } else if (node.style.fontFamily.split(',')[0] && Font.whitelist.includes(node.style.fontFamily.split(',')[0])) {
            fontf = node.style.fontFamily.split(',')[0];
          }
        }
        else {
          if (node.getAttribute('style')?.split('font-family')[1]?.split(';')[0]?.split(':')[1] && Font.whitelist.includes(node.getAttribute('style')?.split('font-family')[1]?.split(';')[0]?.split(':')[1])) {
            fontf = node.getAttribute('style').split('font-family')[1].split(';')[0].split(':')[1];
          }
        }
        if (fontf) {
          delta.map(function (op) {
            if (!op.attributes) op.attributes = {}
            op.attributes['font'] = fontf
            return op
          });
        }

        return delta
      };

      const toolbarElem = this.elementRef.nativeElement.querySelector(
        '[quill-editor-toolbar]'
      )
      let modules: any = Object.assign({}, this.modules() || this.service.config.modules);
      modules = {
        ...modules,
        magicUrl: true,
        table: false,  // disable table module
        clipboard: {
          matchers: [[Node.ELEMENT_NODE, fontFamilyMatcher]]
        },
        'better-table': {
          operationMenu: {
            items: {
              unmergeCells: {
                text: 'Another unmerge cells name'
              }
            },
            color: {
              colors: ['#fff', 'red', 'rgb(0, 0, 0)'],  // colors in operationMenu
              text: 'Background Colors'  // subtitle
            }
          }
        },
        keyboard: {
          bindings: QuillBetterTable.keyboardBindings
        },
        mention: {
          allowedChars: /^[a-zA-Z0-9_]*$/,
          mentionDenotationChars: ["@", "#"],
          positioningStrategy: "fixed",
          blotName: "styled-mention",
          renderLoading: () => {
            return "Loading...";
          },
          renderItem: (data: any) => {
            const div: any = document.createElement("div");
            div.classList.add("quill-mention-item-container");
            /*  const image = document.createElement("img");
             if (this.mentionType === "@") {
               image.src = assetUrl('images/user.svg'); */
            // if (data.email) {
            //   this.collaborationService.getUserProfilePicture(data.email).subscribe({
            //     next: (res: any) => {
            //       image.src = res;
            //     },
            //     error: (err: any) => {
            //       image.src = assetUrl('images/user.svg');
            //     }
            //   })
            // }
            /* }
            else {
              image.src = assetUrl('images/link.svg');
            }
            image.alt = "user";
            image.classList.add("quill-mention-item-container-image"); */
            //div.appendChild(image);
            const splitData: string[] = data.name.split(" ");
            const spanUser = document.createElement("span");
            spanUser.textContent = splitData.length > 1 ? splitData[0][0] + splitData[1][0] : splitData[0][0];
            spanUser.classList.add("quill-mention-item-container-user-name");
            div.appendChild(spanUser);

            const span = document.createElement("span");
            span.textContent = data.label;
            span.classList.add("quill-mention-item-container-name");


            div.appendChild(span);
            if (this.mentionType === "#") {
              const spanType = document.createElement("span");
              spanType.textContent = FileTypesDeleteMapper[data.kind] ? FileTypesDeleteMapper[data.kind].toLowerCase() : "document";
              spanType.classList.add("quill-mention-item-container-type");
              div.appendChild(spanType);
            }

            const spanTitle = document.createElement("span");
            spanTitle.textContent =  data.title || data.jobFamilyName;
            spanTitle.classList.add("quill-mention-item-container-title");
            div.appendChild(spanTitle);

            // div.innerText = data.label;
            return div;
          },
          source: (searchTerm: any, renderList: any, mentionChar: any) => {
            this.renderList = renderList;

            let values: any = [];
            if (mentionChar === "@") {
              this.mentionType = "@";
            } else {
              this.mentionType = "#";
              // values = cloneDeep(this.hashValues);
            }
            if (mentionChar === "@") {
              if (searchTerm.length === 0) {
                renderList([], searchTerm);
              } else {
                this.searchTermSubject.next(searchTerm);
              }
            } else {
              // if (searchTerm.length === 0 && values.length !== 0) {
              //   renderList([], searchTerm);
              // } 
              // else {
              this.searchTermSubject.next(searchTerm);
              // }
            }
          }
        }
      }

      if (toolbarElem) {
        modules.toolbar = toolbarElem
      } else if (modules.toolbar === undefined) {
        modules.toolbar = defaultModules.toolbar
      }

      let placeholder = this.placeholder() !== undefined ? this.placeholder() : this.service.config.placeholder
      if (placeholder === undefined) {
        placeholder = "What's on your mind?"
      }

      const styles = this.styles()
      if (styles) {
        Object.keys(styles).forEach((key: string) => {
          this.renderer.setStyle(this.editorElem, key, styles[key])
        })
      }

      if (this.classes()) {
        this.addClasses(this.classes())
      }

      this.customOptions().forEach((customOption) => {
        const newCustomOption = Quill.import(customOption.import)
        newCustomOption.whitelist = customOption.whitelist
        Quill.register(newCustomOption, true)
      })

      let bounds = this.bounds() && this.bounds() === 'self' ? this.editorElem : this.bounds()
      if (!bounds) {
        bounds = this.service.config.bounds ? this.service.config.bounds : this.document.body
      }

      let debug = this.debug()
      if (!debug && debug !== false && this.service.config.debug) {
        debug = this.service.config.debug
      }

      let readOnly = this.readOnly()
      if (!readOnly && this.readOnly() !== false) {
        readOnly = this.service.config.readOnly !== undefined ? this.service.config.readOnly : false
      }

      let formats = this.formats()
      if (!formats && formats === undefined) {
        formats = this.service.config.formats ? [...this.service.config.formats] : (this.service.config.formats === null ? null : undefined)
      }

      this.zone.runOutsideAngular(() => {
        Quill.register('modules/mention', Mention);
        this.quillEditor = new Quill(this.editorElem, {
          bounds,
          debug,
          formats,
          modules,
          placeholder,
          readOnly,
          registry: this.registry(),
          theme: this.theme() || (this.service.config.theme ? this.service.config.theme : 'snow')
        })
        //this.quillEditor.format('size', '14px');
        if (this.onNativeBlur.observed) {
          // https://github.com/quilljs/quill/issues/2186#issuecomment-533401328
          this.quillEditor.scroll.domNode.addEventListener('blur', () => this.onNativeBlur.next({
            editor: this.quillEditor,
            source: 'dom'
          }))
          // https://github.com/quilljs/quill/issues/2186#issuecomment-803257538
          const toolbar = this.quillEditor.getModule('toolbar') as Toolbar
          toolbar.container?.addEventListener('mousedown', (e) => e.preventDefault())
        }

        if (this.onNativeFocus.observed) {
          this.quillEditor.scroll.domNode.addEventListener('focus', () => this.onNativeFocus.next({
            editor: this.quillEditor,
            source: 'dom'
          }))
        }

        // Set optional link placeholder, Quill has no native API for it so using workaround
        if (this.linkPlaceholder()) {
          const tooltip = (this.quillEditor as any)?.theme?.tooltip
          const input = tooltip?.root?.querySelector('input[data-link]')
          if (input?.dataset) {
            input.dataset.link = this.linkPlaceholder()
          }
        }
      })
      this.quillEditor.root.addEventListener('keydown', evt => {
        if ((evt.code === "Delete" || evt.code === "Backspace") && this.ctrlAPressed) {
          this.resetQuillEditor();
        } else if (evt.code === "Backspace" && this.emptyText) {
          this.resetQuillEditor();
        }
        this.ctrlAPressed = evt.ctrlKey && evt.key === 'a' ? true : false;
      });
      if (this.content) {
        const format = getFormat(this.format(), this.service.config.format)

        if (format === 'text') {
          this.quillEditor.setText(this.content, 'silent')
        } else {
          const valueSetter = this.valueSetter()
          const newValue = valueSetter(this.quillEditor, this.content)
          this.quillEditor.setContents(newValue, 'silent')
        }

        const history = this.quillEditor.getModule('history') as History
        history.clear()
      }

      // initialize disabled status based on this.disabled as default value
      this.setDisabledState()

      this.addQuillEventListeners()

      // The `requestAnimationFrame` triggers change detection. There's no sense to invoke the `requestAnimationFrame` if anyone is
      // listening to the `onEditorCreated` event inside the template, for instance `<quill-view (onEditorCreated)="...">`.
      if (!this.onEditorCreated.observed && !this.onValidatorChanged) {
        return
      }

      // The `requestAnimationFrame` will trigger change detection and `onEditorCreated` will also call `markDirty()`
      // internally, since Angular wraps template event listeners into `listener` instruction. We're using the `requestAnimationFrame`
      // to prevent the frame drop and avoid `ExpressionChangedAfterItHasBeenCheckedError` error.
      raf$().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        if (this.onValidatorChanged) {
          this.onValidatorChanged()
        }
        this.onEditorCreated.emit(this.quillEditor)
      })
    })
  }

  getListBasedOnSearch(values: any[], searchTerm: string) {
    const matches = [];
    for (let i = 0; i < values.length; i++) {
      if (values[i].label.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1) {
        matches.push(values[i]);
      }
    }
    return matches;
  }

  getDocumentsList(searchTerm: any) {
    if (this.atMentionConfig()?.['token']) {
      this.collaborationService.getSharedDocuments(this.atMentionConfig(), searchTerm).subscribe((res: any) => {
        this.hashValues = res;
        this.renderList(searchTerm.length === 0 ? this.hashValues : this.getListBasedOnSearch(this.hashValues, searchTerm), searchTerm);
      });
      return;
    }
    this.collaborationService.getAllDocuments(searchTerm).subscribe((res: any) => {
      this.hashValues = res;
      this.renderList(searchTerm.length === 0 ? this.hashValues : this.getListBasedOnSearch(this.hashValues, searchTerm), searchTerm);
    });
  }

  getUsersList(searchTerm: any,) {
    if (this.atMentionConfig()?.['token']) {
      this.collaborationService.getSharedUsersList(this.atMentionConfig()?.['token'], searchTerm)
        .pipe(
          catchError((err) => {
            this.collaborationService.displayErrorMessage(err, 'Error while retrieving Underwriters. Please try after some time.');
            return EMPTY;
          }),
          map((userList) => userList.filter(user => {
            return (user.collabUser === 1 || !('collabUser' in user))
          }))
        )
        .subscribe((res) => {
          this.renderList(res);
        })
      return;
    }
    this.collaborationService.getUsersList(searchTerm)
      .pipe(
        catchError((err) => {
          this.collaborationService.displayErrorMessage(err, 'Error while retrieving Underwriters. Please try after some time.');
          return EMPTY;
        }),
        map((userList) => userList.filter(user => {
          return (user.collabUser === 1 || !('collabUser' in user))
        }))
      )
      .subscribe((res) => {
        this.renderList(res);
      });
  }

  selectionChangeHandler = (range: Range | null, oldRange: Range | null, source: string) => {
    const trackChanges = this.trackChanges() || this.service.config.trackChanges
    const shouldTriggerOnModelTouched = !range && !!this.onModelTouched && (source === 'user' || trackChanges && trackChanges === 'all')

    // only emit changes when there's any listener
    if (!this.onBlur.observed &&
      !this.onFocus.observed &&
      !this.onSelectionChanged.observed &&
      !shouldTriggerOnModelTouched) {
      return
    }

    this.zone.run(() => {
      if (range === null) {
        this.onBlur.emit({
          editor: this.quillEditor,
          source
        })
      } else if (oldRange === null) {
        this.onFocus.emit({
          editor: this.quillEditor,
          source
        })
      }

      this.onSelectionChanged.emit({
        editor: this.quillEditor,
        oldRange,
        range,
        source
      })

      if (shouldTriggerOnModelTouched) {
        this.onModelTouched()
      }

      this.cd.markForCheck()
    })
  }

  textChangeHandler = (delta: DeltaType, oldDelta: DeltaType, source: string): void => {
    // only emit changes emitted by user interactions
    const text = this.quillEditor.getText()
    const content = this.quillEditor.getContents()

    let html: string | null = this.quillEditor.getSemanticHTML();
    //set flag  based on html for backspace functionality
    this.emptyCheckFlag(html);
    if (text === '\n') {
      setTimeout(() => {
        const getSemanticHTML = this.quillEditor.getSemanticHTML();
        const tableRegex = /<div class="([^"]+)"><table class="([^"]+)"><colgroup><col width="([^"]+)"><\/colgroup><\/table><\/div>/;
        const emptyRegex = /<p(?:\s+\w+="[^"]*")*\s*>\s*<\/p>/;
        if (tableRegex.test(getSemanticHTML) || emptyRegex.test(getSemanticHTML)) {
          this.quillEditor?.setText('', 'user');
          this.quillEditor.setSelection(0);
          this.quillEditor?.focus();
        }
      }, 0);
    }
    if (this.isEmptyValue(html)) {
      this.quillEditor?.setText('', 'user');
      this.quillEditor.setSelection(0);
      this.quillEditor?.focus();
      html = this.defaultEmptyValue()
    }

    const trackChanges = this.trackChanges() || this.service.config.trackChanges
    const shouldTriggerOnModelChange = (source === 'user' || trackChanges && trackChanges === 'all') && !!this.onModelChange

    // only emit changes when there's any listener
    if (!this.onContentChanged.observed && !shouldTriggerOnModelChange) {
      return
    }

    this.zone.run(() => {
      if (shouldTriggerOnModelChange) {
        const valueGetter = this.valueGetter()
        this.onModelChange(
          valueGetter(this.quillEditor)
        )
      }

      this.onContentChanged.emit({
        content,
        delta,
        editor: this.quillEditor,
        html,
        oldDelta,
        source,
        text
      })

      this.cd.markForCheck()
    })
  }

  // eslint-disable-next-line max-len
  editorChangeHandler = (
    event: 'text-change' | 'selection-change',
    current: any | Range | null, old: any | Range | null, source: string
  ): void => {
    // only emit changes when there's any listener
    if (!this.onEditorChanged.observed) {
      return
    }

    // only emit changes emitted by user interactions
    if (event === 'text-change') {
      const text = this.quillEditor.getText()
      const content = this.quillEditor.getContents()

      let html: string | null = this.quillEditor.getSemanticHTML()
      if (this.isEmptyValue(html)) {
        html = this.defaultEmptyValue()
      }

      this.zone.run(() => {
        this.onEditorChanged.emit({
          content,
          delta: current,
          editor: this.quillEditor,
          event,
          html,
          oldDelta: old,
          source,
          text
        })

        this.cd.markForCheck()
      })
    } else {
      this.zone.run(() => {
        this.onEditorChanged.emit({
          editor: this.quillEditor,
          event,
          oldRange: old,
          range: current,
          source
        })

        this.cd.markForCheck()
      })
    }
  }

  ngOnDestroy() {
    this.dispose()

    const mentionModule = this.quillEditor.getModule('mention') as Mention;
    if (mentionModule) {
      mentionModule.hideMentionList();
    }

    this.quillSubscription?.unsubscribe()
    this.quillSubscription = null
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.quillEditor) {
      return
    }
    /* eslint-disable @typescript-eslint/dot-notation */
    if (changes["readOnly"]) {
      this.quillEditor.enable(!changes["readOnly"].currentValue)
    }
    if (changes["placeholder"]) {
      this.quillEditor.root.dataset["placeholder"] =
        changes["placeholder"].currentValue
    }
    if (changes["styles"]) {
      const currentStyling = changes["styles"].currentValue
      const previousStyling = changes["styles"].previousValue

      if (previousStyling) {
        Object.keys(previousStyling).forEach((key: string) => {
          this.renderer.removeStyle(this.editorElem, key)
        })
      }
      if (currentStyling) {
        Object.keys(currentStyling).forEach((key: string) => {
          this.renderer.setStyle(this.editorElem, key, this.styles()[key])
        })
      }
    }
    if (changes["classes"]) {
      const currentClasses = changes["classes"].currentValue
      const previousClasses = changes["classes"].previousValue

      if (previousClasses) {
        this.removeClasses(previousClasses)
      }

      if (currentClasses) {
        this.addClasses(currentClasses)
      }
    }
    // We'd want to re-apply event listeners if the `debounceTime` binding changes to apply the
    // `debounceTime` operator or vice-versa remove it.
    if (changes["debounceTime"]) {
      this.addQuillEventListeners()
    }
    /* eslint-enable @typescript-eslint/dot-notation */
  }

  addClasses(classList: string): void {
    QuillEditorBase.normalizeClassNames(classList).forEach((c: string) => {
      this.renderer.addClass(this.editorElem, c)
    })
  }

  removeClasses(classList: string): void {
    QuillEditorBase.normalizeClassNames(classList).forEach((c: string) => {
      this.renderer.removeClass(this.editorElem, c)
    })
  }

  writeValue(currentValue: any) {

    // optional fix for https://github.com/angular/angular/issues/14988
    if (this.filterNull() && currentValue === null) {
      return
    }

    this.content = currentValue

    if (!this.quillEditor) {
      return
    }

    const format = getFormat(this.format(), this.service.config.format)
    const valueSetter = this.valueSetter()
    const newValue = valueSetter(this.quillEditor, currentValue)

    if (this.compareValues()) {
      const currentEditorValue = this.quillEditor.getContents()
      if (JSON.stringify(currentEditorValue) === JSON.stringify(newValue)) {
        return
      }
    }

    if (currentValue) {
      if (format === 'text') {
        this.quillEditor.setText(currentValue)
      } else {
        this.quillEditor.setContents(newValue)
      }
      return
    }
    this.quillEditor.setText('')

  }

  setDisabledState(isDisabled: boolean = this.disabled): void {
    // store initial value to set appropriate disabled status after ViewInit
    this.disabled = isDisabled
    if (this.quillEditor) {
      if (isDisabled) {
        this.quillEditor.disable()
        this.renderer.setAttribute(this.elementRef.nativeElement, 'disabled', 'disabled')
      } else {
        if (!this.readOnly()) {
          this.quillEditor.enable()
        }
        this.renderer.removeAttribute(this.elementRef.nativeElement, 'disabled')
      }
    }
  }

  registerOnChange(fn: (modelValue: any) => void): void {
    this.onModelChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onModelTouched = fn
  }

  registerOnValidatorChange(fn: () => void) {
    this.onValidatorChanged = fn
  }

  validate() {
    if (!this.quillEditor) {
      return null
    }

    const err: {
      minLengthError?: {
        given: number
        minLength: number
      }
      maxLengthError?: {
        given: number
        maxLength: number
      }
      requiredError?: { empty: boolean }
    } = {}
    let valid = true

    const text = this.quillEditor.getText()
    // trim text if wanted + handle special case that an empty editor contains a new line
    const textLength = this.trimOnValidation() ? text.trim().length : (text.length === 1 && text.trim().length === 0 ? 0 : text.length - 1)
    const deltaOperations: any = this.quillEditor.getContents().ops;
    const onlyEmptyOperation = !!deltaOperations && deltaOperations.length === 1 && ['\n', ''].includes(deltaOperations[0].insert?.toString())

    if (this.minLength() && textLength && textLength < this.minLength()) {
      err.minLengthError = {
        given: textLength,
        minLength: this.minLength()
      }

      valid = false
    }

    if (this.maxLength() && textLength > this.maxLength()) {
      err.maxLengthError = {
        given: textLength,
        maxLength: this.maxLength()
      }

      valid = false
    }

    if (this.required() && !textLength && onlyEmptyOperation) {
      err.requiredError = {
        empty: true
      }

      valid = false
    }

    return valid ? null : err
  }

  private addQuillEventListeners(): void {
    this.dispose()

    // We have to enter the `<root>` zone when adding event listeners, so `debounceTime` will spawn the
    // `AsyncAction` there w/o triggering change detections. We still re-enter the Angular's zone through
    // `zone.run` when we emit an event to the parent component.
    this.zone.runOutsideAngular(() => {
      this.subscription = new Subscription()

      this.subscription.add(
        // mark model as touched if editor lost focus
        fromEvent(this.quillEditor, 'selection-change').subscribe(
          ([range, oldRange, source]) => {
            this.selectionChangeHandler(range as any, oldRange as any, source)
          }
        )
      )

      // The `fromEvent` supports passing JQuery-style event targets, the editor has `on` and `off` methods which
      // will be invoked upon subscription and teardown.
      let textChange$ = fromEvent(this.quillEditor, 'text-change')
      let editorChange$ = fromEvent(this.quillEditor, 'editor-change')

      if (typeof this.debounceTime() === 'number') {
        textChange$ = textChange$.pipe(debounceTime(this.debounceTime()))
        editorChange$ = editorChange$.pipe(debounceTime(this.debounceTime()))
      }

      this.subscription.add(
        // update model if text changes
        textChange$.subscribe(([delta, oldDelta, source]) => {
          this.textChangeHandler(delta as any, oldDelta as any, source)
        })
      )

      this.subscription.add(
        // triggered if selection or text changed
        editorChange$.subscribe(([event, current, old, source]) => {
          this.editorChangeHandler(event as 'text-change' | 'selection-change', current, old, source)
        })
      )
    })
  }

  private dispose(): void {
    if (this.subscription !== null) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  private isEmptyValue(html: string | null) {
    return html === '<p></p>' || html === '<div></div>' || html === '<p><br></p>' || html === '<div><br></div>' || html === '<h1></h1>'
  }

  emptyCheckFlag(html: string | null) {
    const isEditorEmpty = html?.replace(/<\/?[^>]+(>|$)/g, "").trim() === '';
    if (isEditorEmpty) {
      setTimeout(() => {
        this.emptyText = true;
      }, 0);
    }
    else {
      this.emptyText = false;
    }
  }
  resetQuillEditor() {
    this.quillEditor?.setText('', 'user');
    this.quillEditor.setSelection(0);
    this.quillEditor?.focus();
  }
}

@Component({
  encapsulation: ViewEncapsulation.Emulated,
  providers: [
    {
      multi: true,
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => QuillEditorComponent)
    },
    {
      multi: true,
      provide: NG_VALIDATORS,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => QuillEditorComponent)
    }
  ],
  selector: 'quill-editor',
  template: `
    @if (toolbarPosition() !== 'top') {
        <div quill-editor-element></div>
    }

    <ng-content select="[above-quill-editor-toolbar]"></ng-content>
    <ng-content select="[quill-editor-toolbar]"></ng-content>
    <ng-content select="[below-quill-editor-toolbar]"></ng-content>

    @if (toolbarPosition() === 'top') {
        <div quill-editor-element></div>
    }
  `,
  styles: [
    `
    :host {
      display: inline-block;
    }
    `
  ],
  standalone: true
})
export class QuillEditorComponent extends QuillEditorBase { }